'use client';

import {
  Mail,
  Phone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  Globe,
  Share2,
  Layers,
  LayoutGrid,
  Lock,
  Wallet,
  ShieldCheck,
  ArrowRight,
  LogIn,
  Sparkles,
} from 'lucide-react';
import { motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { useAppRouter } from '~/hooks';

export default function HomePage() {
  const router = useAppRouter();

  return (
    <>
      {/* Hero Section */}
      <section className="px-4 py-20 md:py-32 relative overflow-hidden bg-linear-to-br from-background-secondary via-secondary to-accent dark:from-[#1e293b] dark:via-[#312e81] dark:to-[#3730a3]">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-20 right-10 w-72 h-72 bg-linear-to-br from-primary/40 to-[#8b5cf6]/30 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 bg-linear-to-tr from-[#8b5cf6]/20 to-[#ec4899]/20 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Additional accent blobs */}
        <motion.div
          className="absolute top-1/2 left-1/4 w-64 h-64 bg-linear-to-br from-primary/30 to-transparent rounded-full opacity-50 blur-2xl"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* linear Mesh Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utb3BhY2l0eT0iMC4xIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 text-primary dark:opacity-20"></div>

        {/* Floating Sketched Elements */}
        {/* Star sketch top-left */}
        <motion.svg
          className="absolute top-10 left-10 w-16 h-16 text-primary/40"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 15, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M50 10 L55 40 L85 45 L60 65 L70 95 L50 80 L30 95 L40 65 L15 45 L45 40 Z" />
          <circle cx="50" cy="50" r="3" fill="currentColor" />
        </motion.svg>

        {/* Lightning bolt sketch top-right */}
        <motion.svg
          className="absolute top-20 right-20 w-20 h-20 text-primary/50"
          animate={{
            y: [0, 15, 0],
            x: [0, -10, 0],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M55 10 L25 45 L45 48 L35 90 L75 45 L55 43 Z" />
        </motion.svg>

        {/* Checkmark circle sketch bottom-left */}
        <motion.svg
          className="absolute bottom-16 left-16 w-24 h-24 text-primary/30"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="40" strokeDasharray="5,5" />
          <path d="M30 50 L45 65 L70 35" strokeWidth="3" />
        </motion.svg>

        {/* Arrow sketch bottom-right */}
        <motion.svg
          className="absolute bottom-20 right-16 w-28 h-28 text-primary/40"
          animate={{
            x: [0, 10, 0],
            y: [0, -10, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 50 Q50 10 90 50" />
          <path d="M70 30 L90 50 L70 70" />
          <circle cx="15" cy="48" r="3" fill="currentColor" />
        </motion.svg>

        {/* Sparkle sketch middle-left */}
        <motion.svg
          className="absolute top-1/2 left-1/4 w-12 h-12 text-primary/60 hidden lg:block"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M50 10 L50 90 M10 50 L90 50" />
          <path d="M20 20 L80 80 M80 20 L20 80" />
        </motion.svg>

        {/* Scribble circle middle-right */}
        <motion.svg
          className="absolute top-1/3 right-1/4 w-20 h-20 text-primary/35 hidden lg:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
          <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
          <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
        </motion.svg>

        {/* Dotted heart sketch */}
        <motion.svg
          className="absolute bottom-1/3 left-1/3 w-16 h-16 text-primary/45 hidden md:block"
          animate={{
            y: [0, -15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.8,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path
            d="M50 85 C50 85, 20 60, 20 40 C20 25, 30 15, 40 15 C45 15, 50 20, 50 20 C50 20, 55 15, 60 15 C70 15, 80 25, 80 40 C80 60, 50 85, 50 85 Z"
            strokeDasharray="4,4"
          />
        </motion.svg>

        {/* Plus signs */}
        <motion.svg
          className="absolute top-1/4 right-1/3 w-10 h-10 text-primary/50 hidden lg:block"
          animate={{
            rotate: [0, 90, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M50 20 L50 80 M20 50 L80 50" />
        </motion.svg>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-center md:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-card/80 backdrop-blur-sm text-secondary-foreground rounded-full mb-6 border border-card-border shadow-lg">
                  <Zap className="w-4 h-4 text-primary" />
                  <span>Ai Driven B2B Solution</span>
                </div>
              </motion.div>

              <motion.h1
                className="mb-6 text-foreground drop-shadow-sm font-bold"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                The Sales Platform for Smarter Business Marketing
              </motion.h1>

              <motion.p
                className="text-foreground-secondary mb-8 max-w-xl mx-auto md:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Leadbunker is the most efficient way to extract contact
                information from websites, documents, and text. Save hours of
                manual work with our intelligent scraping technology.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Link
                  href="/auth/signup"
                  className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Get Started Free
                </Link>
                <Link
                  href="/auth/signin"
                  className="px-8 py-3 bg-card/80 backdrop-blur-sm text-foreground border border-card-border rounded-lg hover:bg-card transition-colors shadow-md flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </Link>
              </motion.div>

              {/* Floating Stats */}
              <motion.div
                className="mt-12 flex gap-8 justify-center md:justify-start"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <div>
                  <div className="text-foreground drop-shadow-sm">10K+</div>
                  <div className="text-foreground-secondary drop-shadow-sm">
                    Active Users
                  </div>
                </div>
                <div>
                  <div className="text-foreground drop-shadow-sm">1M+</div>
                  <div className="text-foreground-secondary drop-shadow-sm">
                    Leads Scraped
                  </div>
                </div>
                <div>
                  <div className="text-foreground drop-shadow-sm">99.9%</div>
                  <div className="text-foreground-secondary drop-shadow-sm">
                    Accuracy
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Animated Image & Elements */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative z-10"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-card-border">
                  <Image
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=800&fit=crop"
                    alt="Happy person using phone"
                    className="w-full h-auto"
                    width={900}
                    height={100}
                  />
                  {/* linear Overlay */}
                  <div className="absolute inset-0 bg-linear-to-t from-primary/20 to-transparent"></div>
                </div>
              </motion.div>

              {/* Floating Email Icon */}
              <motion.div
                className="absolute -top-8 -left-8 bg-card p-4 rounded-xl shadow-xl border border-card-border"
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <Mail className="w-8 h-8 text-primary" />
              </motion.div>

              {/* Floating Phone Icon */}
              <motion.div
                className="absolute -bottom-8 -right-8 bg-card p-4 rounded-xl shadow-xl border border-card-border"
                animate={{
                  y: [0, 10, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 1.5,
                }}
              >
                <Phone className="w-8 h-8 text-[#10b981]" />
              </motion.div>

              {/* Floating Data Card */}
              <motion.div
                className="absolute top-1/4 -right-12 bg-card p-3 rounded-lg shadow-xl border border-card-border hidden lg:block"
                animate={{
                  x: [0, 10, 0],
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#10b981] rounded-full animate-pulse"></div>
                  <span className="text-foreground-secondary">
                    contact@example.com
                  </span>
                </div>
              </motion.div>

              {/* Floating Stats Card */}
              <motion.div
                className="absolute bottom-1/4 -left-12 bg-card p-3 rounded-lg shadow-xl border border-card-border hidden lg:block"
                animate={{
                  x: [0, -10, 0],
                  y: [0, 10, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 2,
                }}
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#f59e0b]" />
                  <span className="text-foreground-secondary">
                    +127 leads found
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="px-4 py-20 bg-background-secondary relative overflow-hidden">
        {/* Floating Arrow Sketch - Top Left */}
        <motion.svg
          className="absolute top-10 left-10 w-16 h-16 text-primary/40 hidden lg:block"
          animate={{
            x: [0, 20, 0],
            y: [0, -10, 0],
            rotate: [0, 10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 50 Q50 10 90 50" />
          <path d="M70 30 L90 50 L70 70" />
          <circle cx="15" cy="48" r="3" fill="currentColor" />
        </motion.svg>

        {/* Scribble Circle - Top Right */}
        <motion.svg
          className="absolute top-20 right-10 w-20 h-20 text-primary/35 hidden lg:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
          <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
          <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
        </motion.svg>

        {/* Plus Sign - Bottom Left */}
        <motion.svg
          className="absolute bottom-20 left-1/4 w-10 h-10 text-primary/50 hidden md:block"
          animate={{
            rotate: [0, 90, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M50 20 L50 80 M20 50 L80 50" />
        </motion.svg>

        {/* Star Sketch - Bottom Right */}
        <motion.svg
          className="absolute bottom-10 right-1/4 w-14 h-14 text-primary/30 hidden md:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path
            d="M50 10 L60 40 L90 40 L65 60 L75 90 L50 70 L25 90 L35 60 L10 40 L40 40 Z"
            strokeDasharray="3,3"
          />
        </motion.svg>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 md:order-1"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-card-border">
                <Image
                  src="https://images.unsplash.com/photo-1630487656049-6db93a53a7e9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwb2ZmaWNlfGVufDF8fHx8MTc2NDk5MDkzOHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="About Leadbunker"
                  className="w-full h-auto"
                  width={500}
                  height={100}
                />
                {/* Decorative linear overlay */}
                <div className="absolute inset-0 bg-linear-to-tr from-primary/10 via-transparent to-[#8b5cf6]/10"></div>
              </div>
            </motion.div>

            {/* Right Column - Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-1 md:order-2"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-6">
                <Shield className="w-4 h-4" />
                <span>About Us</span>
              </div>

              <h2 className="mb-6 text-foreground font-bold">
                Empowering Businesses with Smart Data Solutions
              </h2>

              <p className="text-foreground-secondary mb-6">
                Leadbunker was founded with a simple mission: to make contact
                data extraction accessible, efficient, and reliable for
                businesses of all sizes. We understand that quality leads are
                the lifeblood of any successful marketing campaign.
              </p>

              <p className="text-foreground-secondary mb-8">
                Our AI-driven platform combines cutting-edge technology with
                intuitive design, enabling professionals to discover and
                validate contact information at scale. From solo entrepreneurs
                to enterprise teams, we&apos;re trusted by thousands worldwide
                to deliver accurate, actionable data that drives growth.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#10b981]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#10b981]" />
                  </div>
                  <div>
                    <div className="text-foreground font-bold mb-1">
                      Trusted
                    </div>
                    <div className="text-foreground-secondary">
                      Industry-leading accuracy
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-foreground font-bold mb-1">Fast</div>
                    <div className="text-foreground-secondary">
                      Real-time processing
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#8b5cf6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#8b5cf6]" />
                  </div>
                  <div>
                    <div className="text-foreground font-bold mb-1">Secure</div>
                    <div className="text-foreground-secondary">
                      Privacy-first approach
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#f59e0b]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-[#f59e0b]" />
                  </div>
                  <div>
                    <div className="text-foreground font-bold mb-1">Global</div>
                    <div className="text-foreground-secondary">
                      Worldwide coverage
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose LEADBUNKER Section */}
      <section className="px-4 py-20 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="mb-4 text-foreground font-bold">
              Why Choose Leadbunker?
            </h2>
            <p className="text-foreground-secondary">
              Built for Professional businesses that wants to grow and save
              time.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="mb-2 text-foreground">Email Discovery</h3>
              <p className="text-foreground-secondary">
                Accurately identify and extract email addresses from any text or
                webpage with our advanced pattern recognition.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#10b981]/10 rounded-lg flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-[#10b981]" />
              </div>
              <h3 className="mb-2 text-foreground">Phone Detection</h3>
              <p className="text-foreground-secondary">
                Extract phone numbers in multiple formats from different
                countries with intelligent formatting.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#8b5cf6]/10 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-[#8b5cf6]" />
              </div>
              <h3 className="mb-2 text-foreground">Lightning Fast</h3>
              <p className="text-foreground-secondary">
                Process thousands of pages in seconds. Our optimized engine
                handles large-scale extraction effortlessly.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-[#3b82f6]" />
              </div>
              <h3 className="mb-2 text-foreground">Fast Accurate Validation</h3>
              <p className="text-foreground-secondary">
                Verify email addresses and phone numbers in real-time to ensure
                data quality and deliverability.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#06b6d4]/10 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-[#06b6d4]" />
              </div>
              <h3 className="mb-2 text-foreground">
                Universal Website Support
              </h3>
              <p className="text-foreground-secondary">
                Compatible with any website architecture. Extract data from
                static sites, dynamic pages, and SPAs.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#ec4899]/10 rounded-lg flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-[#ec4899]" />
              </div>
              <h3 className="mb-2 text-foreground">Social Media Discovery</h3>
              <p className="text-foreground-secondary">
                Find and extract contact information from LinkedIn, Twitter,
                Facebook and other social platforms.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#f59e0b]/10 rounded-lg flex items-center justify-center mb-4">
                <Layers className="w-6 h-6 text-[#f59e0b]" />
              </div>
              <h3 className="mb-2 text-foreground">Bulk Processing</h3>
              <p className="text-foreground-secondary">
                Upload and process multiple URLs, documents, or text files
                simultaneously for maximum efficiency.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#14b8a6]/10 rounded-lg flex items-center justify-center mb-4">
                <LayoutGrid className="w-6 h-6 text-[#14b8a6]" />
              </div>
              <h3 className="mb-2 text-foreground">Organized Data</h3>
              <p className="text-foreground-secondary">
                Export your results in structured formats with smart
                categorization and deduplication.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#ef4444]/10 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-[#ef4444]" />
              </div>
              <h3 className="mb-2 text-foreground">GDPR Compliant</h3>
              <p className="text-foreground-secondary">
                Fully compliant with data protection regulations. All data
                processing follows strict privacy standards.
              </p>
            </div>

            <div className="p-6 bg-secondary rounded-xl border border-card-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#a855f7]/10 rounded-lg flex items-center justify-center mb-4">
                <Wallet className="w-6 h-6 text-[#a855f7]" />
              </div>
              <h3 className="mb-2 text-foreground">Fast Crypto Payment</h3>
              <p className="text-foreground-secondary">
                Accept Bitcoin, Ethereum, and other cryptocurrencies for
                instant, secure, and anonymous transactions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 py-20 bg-background-secondary">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="mb-6 text-foreground font-bold">
                Built for Scale
              </h2>
              <p className="text-foreground-secondary mb-8">
                Whether you&apos;re a freelancer or an enterprise, Leadbunker
                adapts to your needs with powerful features and reliable
                performance.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-foreground mb-1">Bulk Processing</h4>
                    <p className="text-foreground-secondary">
                      Upload multiple URLs or documents at once
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-foreground mb-1">Export Options</h4>
                    <p className="text-foreground-secondary">
                      Download results in CSV, JSON, or Excel format
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-foreground mb-1">Duplicate Removal</h4>
                    <p className="text-foreground-secondary">
                      Automatically filter out duplicate entries
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-xl border border-card-border">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-6 h-6 text-primary" />
                <h3 className="text-foreground font-bold">Privacy First</h3>
              </div>
              <p className="text-foreground-secondary mb-6">
                We take data privacy seriously. All scraping is done locally,
                and we never store your extracted data on our servers.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-secondary rounded-lg border border-card-border">
                  <TrendingUp className="w-8 h-8 text-primary mb-2" />
                  <div className="text-foreground">99.9%</div>
                  <div className="text-foreground-secondary">Uptime</div>
                </div>
                <div className="p-4 bg-secondary rounded-lg border border-card-border">
                  <Shield className="w-8 h-8 text-[#10b981] mb-2" />
                  <div className="text-foreground">256-bit</div>
                  <div className="text-foreground-secondary">Encryption</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-20 bg-linear-to-br from-primary via-[#8b5cf6] to-[#ec4899] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>

        {/* Floating Sketched Elements */}
        {/* Star sketch top-left */}
        <motion.svg
          className="absolute top-10 left-10 w-16 h-16 text-white/40"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 15, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M50 10 L55 40 L85 45 L60 65 L70 95 L50 80 L30 95 L40 65 L15 45 L45 40 Z" />
          <circle cx="50" cy="50" r="3" fill="currentColor" />
        </motion.svg>

        {/* Lightning bolt sketch top-right */}
        <motion.svg
          className="absolute top-20 right-20 w-20 h-20 text-white/50"
          animate={{
            y: [0, 15, 0],
            x: [0, -10, 0],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M55 10 L25 45 L45 48 L35 90 L75 45 L55 43 Z" />
        </motion.svg>

        {/* Checkmark circle sketch bottom-left */}
        <motion.svg
          className="absolute bottom-16 left-16 w-24 h-24 text-white/30"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="40" strokeDasharray="5,5" />
          <path d="M30 50 L45 65 L70 35" strokeWidth="3" />
        </motion.svg>

        {/* Arrow sketch bottom-right */}
        <motion.svg
          className="absolute bottom-20 right-16 w-28 h-28 text-white/40"
          animate={{
            x: [0, 10, 0],
            y: [0, -10, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 50 Q50 10 90 50" />
          <path d="M70 30 L90 50 L70 70" />
          <circle cx="15" cy="48" r="3" fill="currentColor" />
        </motion.svg>

        {/* Sparkle sketch middle-left */}
        <motion.svg
          className="absolute top-1/2 left-1/4 w-12 h-12 text-white/60 hidden lg:block"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M50 10 L50 90 M10 50 L90 50" />
          <path d="M20 20 L80 80 M80 20 L20 80" />
        </motion.svg>

        {/* Scribble circle middle-right */}
        <motion.svg
          className="absolute top-1/3 right-1/4 w-20 h-20 text-white/35 hidden lg:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
          <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
          <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
        </motion.svg>

        {/* Dotted heart sketch */}
        <motion.svg
          className="absolute bottom-1/3 left-1/3 w-16 h-16 text-white/45 hidden md:block"
          animate={{
            y: [0, -15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.8,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path
            d="M50 85 C50 85, 20 60, 20 40 C20 25, 30 15, 40 15 C45 15, 50 20, 50 20 C50 20, 55 15, 60 15 C70 15, 80 25, 80 40 C80 60, 50 85, 50 85 Z"
            strokeDasharray="4,4"
          />
        </motion.svg>

        {/* Plus signs */}
        <motion.svg
          className="absolute top-1/4 right-1/3 w-10 h-10 text-white/50 hidden lg:block"
          animate={{
            rotate: [0, 90, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M50 20 L50 80 M20 50 L80 50" />
        </motion.svg>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.h2
            className="mb-4 text-white font-bold"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Ready to Get Started?
          </motion.h2>
          <motion.p
            className="text-white/95 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Join thousands of professionals who trust Leadbunker for their data
            extraction needs.
          </motion.p>
          <motion.button
            onClick={() => {
              router.push('/auth/signup');
            }}
            className="px-8 py-3 bg-white text-primary rounded-lg hover:bg-white/95 transition-all shadow-xl hover:shadow-2xl hover:scale-105 inline-flex items-center justify-center gap-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Start Scraping Now
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>
      </section>
    </>
  );
}
